import { ActionIcon, Group, Tooltip, Chip } from "@mantine/core";
import {
  IconCancel,
  IconEdit,
  IconExclamationMark,
  IconEye,
} from "@tabler/icons-react";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import type { ITarea } from "../../../modelos/Tarea/entidades/ITarea";
import { EnumTipoTarea } from "../enums/enumTipoTarea";
import { EnumEstadoTarea, EstadoTareaColors, EstadoTareaIconos } from "../enums/enumEstadoTarea";
import type { ExcelColumn } from "../../../globales/funciones/exportarExcel";

interface ColumnasTablaTareaParams {
  manejarEdicionTarea: (tarea: ITarea, visualizacion?: boolean) => void;
  manejarCancelacionTarea: (tarea: ITarea) => void;
  estadoModalExportacionExcel: boolean;
}

export const formatearFecha = (fecha: string | Date | number): string => {
  if (!fecha) return "";

  try {
    // Si llega un objeto Date o número (timestamp), lo formateamos usando Intl
    if (fecha instanceof Date || typeof fecha === 'number') {
      const fechaObjeto = new Date(fecha);
      const fechaFormateada = new Intl.DateTimeFormat("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/Bogota",
      }).format(fechaObjeto);
      return fechaFormateada.replace(",", "");
    }

    // Para cadenas: si contienen información de zona horaria (Z o offset),
    // parseamos como fecha y convertimos a America/Bogota. Si NO contienen zona,
    // asumimos que la hora ya está en la zona local (America/Bogota) y la
    // formateamos manualmente sin convertirla (evitamos restar 5 horas).
    const fechaString = String(fecha);

    const tieneZona = /Z$|[+\-]\d{2}:?\d{2}$/.test(fechaString);

    if (tieneZona) {
      const fechaObjeto = new Date(fechaString);
      const fechaFormateada = new Intl.DateTimeFormat("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/Bogota",
      }).format(fechaObjeto);
      return fechaFormateada.replace(",", "");
    }

    // Sin zona: formateamos directamente a partir de la cadena ISO esperada
    // formato esperado: YYYY-MM-DDTHH:mm[:ss]
    const m = fechaString.match(/^(\d{4})-?(\d{2})-?(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (m) {
      const [, y, mo, d, hh, mm] = m;
      const hora = Number(hh);
      const minutos = Number(mm);
      const hora12 = hora % 12 === 0 ? 12 : hora % 12;
      const sufijo = hora < 12 ? 'a. m.' : 'p. m.';
      const hhStr = String(hora12).padStart(2, '0');
      const mmStr = String(minutos).padStart(2, '0');
      return `${d}/${mo}/${y} ${hhStr}:${mmStr} ${sufijo}`;
    }

    // Fallback: intentar parse con Date
    const fechaObjetoFallback = new Date(fechaString);
    const fechaFormateadaFallback = new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/Bogota",
    }).format(fechaObjetoFallback);
    return fechaFormateadaFallback.replace(",", "");
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return "";
  }
};


export const formatearFechaDesdeUTC = (fechaBackend: string): string => {
  if (!fechaBackend) return "";

  // Forzamos a que JS la trate como UTC
  const fechaUtc = new Date(`${fechaBackend}Z`);

  return fechaUtc.toLocaleString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};


export const columnasTablaTarea = ({
  manejarEdicionTarea,
  manejarCancelacionTarea,
  estadoModalExportacionExcel
}: ColumnasTablaTareaParams): ColumnDef<ITarea>[] => [
    ...(!estadoModalExportacionExcel
      ? [{
        id: "actions",
        header: "Acciones",
        cell: ({ row }: CellContext<ITarea, unknown>) => {
          const fila = row;
          return (
            <Group gap={4} justify="center">

              {(fila.original.EstadoTareaId !== EnumEstadoTarea.Exitoso &&
                fila.original.EstadoTareaId !== EnumEstadoTarea.EnCurso &&
                fila.original.EstadoTareaId !== EnumEstadoTarea.Cancelado &&
                fila.original.EstadoTareaId !== EnumEstadoTarea.Fallido &&
                fila.original.EstadoTareaId !== EnumEstadoTarea.PendienteFirma) && (
                  <Tooltip
                    label='Editar'
                    withinPortal
                    openDelay={100}
                    zIndex={9999}
                    style={{ fontSize: '10px', padding: '3px 5px' }}
                    withArrow>
                    <ActionIcon
                      variant="transparent"
                      radius="sm"
                      size="sm"
                      onClick={() => manejarEdicionTarea(fila.original)}
                    >
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}

              {(fila.original.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                fila.original.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                fila.original.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                fila.original.EstadoTareaId === EnumEstadoTarea.Fallido ||
                fila.original.EstadoTareaId === EnumEstadoTarea.PendienteFirma) && (
                  <Tooltip
                    label='Ver'
                    withinPortal
                    openDelay={100}
                    zIndex={9999}
                    style={{ fontSize: '10px', padding: '3px 5px' }}
                    withArrow>
                    <ActionIcon
                      variant="transparent"
                      radius="sm"
                      size="sm"
                      onClick={() => manejarEdicionTarea(fila.original, true)}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}

              {(fila.original.EstadoTareaId !== EnumEstadoTarea.Exitoso &&
              fila.original.EstadoTareaId !== EnumEstadoTarea.Cancelado &&
              fila.original.EstadoTareaId !== EnumEstadoTarea.EnCurso &&
              fila.original.EstadoTareaId !== EnumEstadoTarea.PendienteFirma) && (
              <Tooltip
                withinPortal
                openDelay={100}
                zIndex={9999}
                style={{ fontSize: '10px', padding: '3px 5px' }}
                label="Cancelar"
                withArrow>
                <ActionIcon
                  variant="transparent"
                  radius="sm"
                  size="sm"
                  onClick={() => manejarCancelacionTarea(fila.original)}
                >
                  <IconCancel size={18} />
                </ActionIcon>
              </Tooltip>
            )}

            </Group>
          )
        },
      }]
      : []),

    { accessorKey: "Id", header: "Código" },
    { accessorKey: "Equipo.Proyecto.Nombre", header: "Proyecto" },
    { accessorKey: "Equipo.Proyecto.Cliente.Nombre", header: "Cliente" },
    { accessorKey: "Equipo.Codigo", header: "Código Equipo" },
    { accessorKey: "Equipo.Nombre", header: "Nombre Equipo" },
    { accessorKey: "Equipo.Proyecto.Contrato.Numero", header: "Contrato" },
    {
      // accessorKey: "Tipo",
      header: "Tipo Tarea",
      cell: ({ row }) => {
        if (row.original.TipoTarea?.Id === EnumTipoTarea.Emergencia) {
          return (
            <Chip icon={<IconExclamationMark />} checked defaultChecked style={{ textTransform: "none" }} color="#f58585" variant="filled" size="xs">
              {row.original.TipoTarea?.Nombre || ''}
            </Chip>
          );
        }

        return row.original.TipoTarea?.Nombre || '';
      }
    },
    { accessorKey: "UsuarioAsignado.NombreCompleto", header: "Técnico" },
    {
      accessorKey: "EstadoTareaId",
      header: "Estado",
      cell: ({ row }) => {

        const Icono = EstadoTareaIconos[row.original.EstadoTareaId as EnumEstadoTarea];
        if (row.original.EstadoTareaId) {
          return (
            <Chip
              color={EstadoTareaColors[row.original.EstadoTareaId as EnumEstadoTarea]}
              variant="filled"
              checked
              icon={<Icono size={12} />}
              size="xs"
              style={{ textTransform: "none" }}
            >
              {row.original.EstadoServicio?.Nombre || ''}
            </Chip>
          );

        } return '';

      }
    },
    { accessorKey: "FechaCreacion", header: "Fecha Creación", cell: ({ row }) => `${formatearFecha(row.original.CreadaUtc)}` },
    { accessorKey: "FechaInicio", header: "Fecha Inicio Proyectada", cell: ({ row }) => `${formatearFecha(row.original.FechaInicio)}` },
    { accessorKey: "FechaFin", header: "Fecha Fin Proyectada", cell: ({ row }) => `${formatearFecha(row.original.FechaFin)}` },
    { accessorKey: "LLegadaPunto", header: "Fecha Inicio", cell: ({ row }) => `${formatearFecha(row.original.LLegadaPunto)}` },
    { accessorKey: "FinalizacionPunto", header: "Fecha Fin", cell: ({ row }) => `${formatearFecha(row.original.FinalizacionPunto)}` },

  ];


export const columnasExcel: ExcelColumn<ITarea>[] = [
  { header: "Código", accessorKey: "Id" },
  { header: "Proyecto", accessorKey: "Equipo.Proyecto.Nombre" },
  { header: "Cliente", accessorKey: "Equipo.Proyecto.Cliente.Nombre" },
  { header: "Código Equipo", accessorKey: "Equipo.Codigo" },
  { header: "Nombre Equipo", accessorKey: "Equipo.Nombre" },
  { header: "Contrato", accessorKey: "Equipo.Proyecto.Contrato.Numero" },
  { accessorKey: "UsuarioAsignado.NombreCompleto", header: "Técnico" },
  {
    header: "Tipo Tarea",
    valueGetter: (row) => row.TipoTarea?.Nombre ?? "",
  },
  {
    header: "Estado",
    valueGetter: (row) => row.EstadoServicio?.Nombre ?? "",
  },
  {
    header: "Fecha Creación",
    valueGetter: (row) => formatearFechaDesdeUTC(row.CreadaUtc),
  },
  { header: "Fecha Inicio Proyectada", valueGetter: (row) => `${formatearFecha(row.FechaInicio)}` },
  { header: "Fecha Fin Proyectada", valueGetter: (row) => `${formatearFecha(row.FechaFin)}` },
  { header: "Fecha Inicio", valueGetter: (row) => `${formatearFechaDesdeUTC(row.LLegadaPunto)}` },
  { header: "Fecha Fin", valueGetter: (row) => `${formatearFechaDesdeUTC(row.FinalizacionPunto)}` },
];